import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopBar } from "@/components/TopBar";
import { ReadAloudButton } from "@/components/ReadAloudButton";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, SpellCheck, Plus, Trash2, Sparkles, ListPlus } from "lucide-react";

export default function SpellingBankPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isParent = user?.role === "Parent";
  const [students, setStudents] = useState([]);
  const [sid, setSid] = useState("");
  const [words, setWords] = useState([]);
  const [word, setWord] = useState("");
  const [clue, setClue] = useState("");
  const [mode, setMode] = useState("single"); // single | bulk
  const [bulk, setBulk] = useState("");

  const targetId = isParent ? sid : user?.id;

  const load = useCallback(() => {
    if (!targetId) return;
    api.get(`/spelling-words?student_id=${targetId}`).then((r) => setWords(r.data.words));
  }, [targetId]);

  useEffect(() => {
    if (isParent) api.get("/students").then((r) => { setStudents(r.data); if (r.data[0]) setSid(r.data[0].id); });
  }, [isParent]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!word.trim()) return toast.error("Type a word to add.");
    if (!sid) return toast.error("Choose a student.");
    try {
      await api.post("/spelling-words", { student_id: sid, word, clue });
      setWord(""); setClue(""); toast.success("Word added to the Spelling Bee!"); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const remove = async (id) => { await api.delete(`/spelling-words/${id}`); load(); };

  const addBulk = async () => {
    if (!bulk.trim()) return toast.error("Paste some words first.");
    if (!sid) return toast.error("Choose a student.");
    try {
      const { data } = await api.post("/spelling-words/bulk", { student_id: sid, text: bulk });
      setBulk(""); toast.success(`Added ${data.added} word${data.added === 1 ? "" : "s"}!`); load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <TopBar subtitle="Spelling Words" />
      <main className="max-w-3xl mx-auto px-6 md:px-8 py-10">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <button onClick={() => navigate(isParent ? "/parent" : "/student")} className="flex items-center gap-2 text-sm text-[#6B7280] hover:text-[#3D405B]"><ArrowLeft size={16} /> Back</button>
          {isParent && (
            <select data-testid="spelling-student" value={sid} onChange={(e) => setSid(e.target.value)}
              className="rounded-full border border-[#EAE6DF] bg-white px-5 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E07A5F]">
              {students.map((s) => <option key={s.id} value={s.id}>{s.first_name}</option>)}
            </select>
          )}
        </div>

        <div className="flex items-center gap-3 mb-2">
          <span className="grid place-items-center w-11 h-11 rounded-2xl text-white bg-[#3D405B]"><SpellCheck size={20} /></span>
          <h1 className="font-head text-4xl font-bold text-[#3D405B]">Spelling Word Bank</h1>
        </div>
        <p className="text-[#6B7280] mb-8">Add your own words for the <b>Spelling Bee</b> mini-game. They'll show up for {isParent ? "your child" : "you"} to practice — with a clue and read-aloud. Your custom words appear first, then lesson vocabulary.</p>

        {isParent && (
          <div className="rounded-2xl bg-white border border-[#EAE6DF] p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <button data-testid="mode-single" onClick={() => setMode("single")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "single" ? "bg-[#3D405B] text-white" : "border border-[#EAE6DF] text-[#3D405B] hover:border-[#3D405B]"}`}>
                One at a time
              </button>
              <button data-testid="mode-bulk" onClick={() => setMode("bulk")}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${mode === "bulk" ? "bg-[#3D405B] text-white" : "border border-[#EAE6DF] text-[#3D405B] hover:border-[#3D405B]"}`}>
                Paste a list
              </button>
            </div>
            {mode === "single" ? (
              <>
                <input data-testid="spelling-word-input" value={word} onChange={(e) => setWord(e.target.value)} placeholder="Word (e.g. because)"
                  className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F] mb-3" />
                <input data-testid="spelling-clue-input" value={clue} onChange={(e) => setClue(e.target.value)} placeholder="Clue / meaning (optional — helps them guess)"
                  className="w-full rounded-xl border border-[#EAE6DF] px-4 py-2.5 outline-none focus:ring-2 focus:ring-[#E07A5F] mb-3" />
                <button data-testid="add-spelling-word-btn" onClick={add} className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105">
                  <Plus size={16} /> Add word
                </button>
              </>
            ) : (
              <>
                <p className="text-sm text-[#6B7280] mb-2">Paste this week's spelling list — one word per line (or separated by commas). We'll add them all at once.</p>
                <textarea data-testid="spelling-bulk-input" value={bulk} onChange={(e) => setBulk(e.target.value)}
                  placeholder={"because\nfriend\nbeautiful\nWednesday\n…"}
                  className="w-full min-h-[140px] rounded-xl border border-[#EAE6DF] px-4 py-3 outline-none focus:ring-2 focus:ring-[#E07A5F] mb-3" />
                <button data-testid="add-bulk-btn" onClick={addBulk} className="flex items-center gap-2 rounded-full bg-[#3D405B] text-white px-6 py-2.5 font-semibold transition-transform hover:scale-105">
                  <ListPlus size={16} /> Add all words
                </button>
              </>
            )}
          </div>
        )}

        <div className="space-y-3">
          {words.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[#EAE6DF] p-8 text-center text-[#6B7280]">
              <Sparkles size={20} className="mx-auto mb-2 text-[#F4A261]" />
              No custom words yet{isParent ? " — add one above." : "."} The Spelling Bee will still use lesson vocabulary.
            </div>
          )}
          {words.map((w, i) => (
            <motion.div key={w.id} data-testid={`spelling-word-${w.id}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
              className="rounded-2xl bg-white border border-[#EAE6DF] p-5 flex items-start justify-between gap-3">
              <div>
                <p className="font-head font-semibold text-lg text-[#3D405B]">{w.word}</p>
                {w.clue && <p className="text-sm text-[#6B7280] mt-0.5">{w.clue}</p>}
                <div className="mt-2"><ReadAloudButton testid={`spelling-word-read-${w.id}`} label="Hear it" text={w.word} tone="#3D405B" /></div>
              </div>
              {isParent && (
                <button data-testid={`delete-spelling-word-${w.id}`} onClick={() => remove(w.id)} className="text-[#6B7280] hover:text-[#E07A5F] transition-colors shrink-0"><Trash2 size={15} /></button>
              )}
            </motion.div>
          ))}
        </div>
      </main>
    </div>
  );
}
